import { useState, useCallback, useRef } from 'react';
import { useSnackbar } from 'notistack';
import axios from 'axios';
import { config } from '../config';
import { getIdJwtToken } from '../utils/cognitoTokens';
import { 
  validateFiles, 
  generateFileId, 
  createPreviewUrl, 
  cleanupPreviewUrl,
  isImageFile 
} from '../libs/fileAttachmentUtils';

/**
 * Custom hook for managing file uploads in QPilot Chatbot
 */
const parseS3XmlError = (raw) => {
  if (typeof raw !== 'string' || !raw.trim().startsWith('<')) {
    return null;
  }

  const codeMatch = raw.match(/<Code>([^<]+)<\/Code>/i);
  const messageMatch = raw.match(/<Message>([^<]+)<\/Message>/i);

  if (!codeMatch && !messageMatch) {
    return null;
  }

  return {
    code: codeMatch?.[1] || '',
    message: messageMatch?.[1] || ''
  };
};

const getUploadFailureMessage = (error, fileName) => {
  const status = error?.response?.status;
  const responseData = error?.response?.data;
  const backendErrorRaw = typeof responseData === 'string'
    ? responseData
    : responseData?.error || responseData?.code || responseData?.message;
  const backendError = typeof backendErrorRaw === 'string' ? backendErrorRaw.toLowerCase() : '';
  const s3XmlError = parseS3XmlError(backendErrorRaw);

  if (status === 401) {
    return `Upload failed for ${fileName}: authentication error. Please refresh and sign in again.`;
  }

  if (status === 403) {
    if (s3XmlError?.code === 'SignatureDoesNotMatch' || s3XmlError?.code === 'RequestExpired') {
      return `Upload failed for ${fileName}: presigned upload signature is invalid or expired.`;
    }
    if (s3XmlError?.code === 'AccessDenied' && s3XmlError?.message) {
      return `Upload failed for ${fileName}: ${s3XmlError.message}`;
    }
    if (s3XmlError?.code === 'InvalidArgument' && s3XmlError?.message) {
      return `Upload failed for ${fileName}: ${s3XmlError.message}`;
    }
    if (s3XmlError?.code === 'AuthorizationHeaderMalformed' || s3XmlError?.code === 'InvalidRequest') {
      return `Upload failed for ${fileName}: presign signing configuration is incompatible for this bucket region.`;
    }

    if (backendError.includes('signaturedoesnotmatch') || backendError.includes('request has expired')) {
      return `Upload failed for ${fileName}: upload signature is invalid or expired. Please retry the upload.`;
    }
    if (backendError.includes('accessdenied')) {
      return `Upload failed for ${fileName}: S3 access denied for this presigned upload.`;
    }
    return `Upload failed for ${fileName}: upload was denied by S3 (403).`;
  }

  if (status === 400) {
    if (backendError.includes('unsupported_format')) {
      return `Upload failed for ${fileName}: unsupported format. Use PNG, JPG, WEBP, or GIF.`;
    }
    if (backendError.includes('too_large') || backendError.includes('size')) {
      return `Upload failed for ${fileName}: image exceeds size limits.`;
    }
    if (backendError.includes('too_many') || backendError.includes('max_images')) {
      return `Upload failed for ${fileName}: too many images attached.`;
    }
    if (backendError.includes('total_size')) {
      return `Upload failed for ${fileName}: total image size limit exceeded.`;
    }
    if (backendErrorRaw) {
      return `Upload failed for ${fileName}: ${backendErrorRaw}`;
    }
    return `Upload failed for ${fileName}: request was rejected by the server.`;
  }

  if (error?.message) {
    return `Upload failed for ${fileName}: ${error.message}`;
  }

  return `Upload failed for ${fileName}.`;
};

const isNonRetryableUploadError = (error) => {
  const status = error?.response?.status;
  const message = (error?.message || '').toLowerCase();
  const authSessionFailure =
    message.includes('not authenticated') ||
    message.includes('no current user') ||
    message.includes('no user');

  return status === 400 || status === 401 || status === 403 || authSessionFailure;
};

export const useFileUploads = (options = {}) => {
  const [files, setFiles] = useState([]);
  const { enqueueSnackbar } = useSnackbar();
  const retryCountRef = useRef(new Map());
  const {
    maxFiles,
    maxTotalSize,
    allowedCategories,
    presignPath = '/chat/attachments/presign',
    maxRetries = 3
  } = options;

  const addFiles = useCallback((newFiles) => {
    const fileArray = Array.from(newFiles);
    
    // Validate files
    const validation = validateFiles(fileArray, files, {
      maxFiles,
      maxTotalSize,
      allowedCategories
    });
    if (!validation.valid) {
      enqueueSnackbar(validation.error, { variant: 'error' });
      return;
    }

    // Create pending file objects
    const pendingFiles = fileArray.map((file) => ({
      id: generateFileId(),
      file,
      status: 'pending',
      progress: 0,
      previewUrl: createPreviewUrl(file),
      uploadedUrl: null,
      name: file.name,
      size: file.size,
      mime: file.type,
      isImage: isImageFile(file)
    }));

    setFiles(prev => [...prev, ...pendingFiles]);

    // Start uploads
    pendingFiles.forEach(pendingFile => {
      uploadFile(pendingFile);
    });
  }, [files, maxFiles, maxTotalSize, allowedCategories, enqueueSnackbar]);

  const uploadFile = useCallback(async (pendingFile) => {
    try {
      // Update status to uploading
      setFiles(prev => prev.map(f => 
        f.id === pendingFile.id 
          ? { ...f, status: 'uploading', progress: 0 }
          : f
      ));

      // Step 1: Get presigned URL
      const backendHost = config.REACT_APP_BACKEND_HOST || config.backend?.API_HOST;
      if (!backendHost) {
        throw new Error('Attachment upload backend host is not configured');
      }

      const token = await getIdJwtToken();

      const presignUrl = `${backendHost.replace(/\/$/, '')}${presignPath}`;
      const presignResponse = await axios.post(presignUrl, {
        name: pendingFile.file.name,
        size: pendingFile.file.size,
        type: pendingFile.file.type
      }, {
        headers: {
          Authorization: token,
          'Content-Type': 'application/json'
        }
      });

      const responseData = presignResponse.data || {};
      const uploadConfig = responseData.upload || responseData.aws_url || null;
      const uploadUrl = uploadConfig?.url || responseData.uploadUrl;
      const uploadFields = uploadConfig?.fields || null;
      const fileUrlFromResponse =
        responseData.fileUrl ||
        responseData.file_url ||
        responseData.url ||
        responseData.file?.image_url ||
        responseData.file?.url ||
        responseData.attachment?.image_url ||
        responseData.attachment?.url;
      const inferredFileUrl = uploadFields?.key && uploadUrl
        ? `${uploadUrl.replace(/\/$/, '')}/${uploadFields.key}`
        : null;
      const fileUrl = fileUrlFromResponse || inferredFileUrl;

      if (!uploadUrl) {
        throw new Error('Presign response missing upload URL');
      }

      // Step 2: Upload binary directly to S3
      if (uploadFields) {
        const form = new FormData();
        Object.entries(uploadFields).forEach(([key, value]) => form.append(key, value));
        form.append('file', pendingFile.file);

        // Use fetch to avoid implicit client headers on presigned S3 form-post uploads.
        setFiles(prev => prev.map(f =>
          f.id === pendingFile.id
            ? { ...f, progress: 95 }
            : f
        ));

        const s3Response = await fetch(uploadUrl, {
          method: 'POST',
          body: form
        });

        if (!s3Response.ok) {
          const s3ErrorText = await s3Response.text().catch(() => '');
          const s3UploadError = new Error(`S3 upload failed with status ${s3Response.status}`);
          s3UploadError.response = {
            status: s3Response.status,
            data: s3ErrorText
          };
          throw s3UploadError;
        }
      } else {
        // Backward compatibility path for older presign response shapes
        await axios.put(uploadUrl, pendingFile.file, {
          headers: {
            'Content-Type': pendingFile.file.type,
          },
          onUploadProgress: (progressEvent) => {
            const total = progressEvent.total || pendingFile.file.size || 1;
            const progress = Math.min(99, Math.round((progressEvent.loaded * 100) / total));
            setFiles(prev => prev.map(f => 
              f.id === pendingFile.id 
                ? { ...f, progress }
                : f
            ));
          }
        });
      }

      if (!fileUrl) {
        throw new Error('Presign response missing file URL');
      }

      // Step 3: Mark as completed
      setFiles(prev => prev.map(f => 
        f.id === pendingFile.id 
          ? { ...f, status: 'done', progress: 100, uploadedUrl: fileUrl }
          : f
      ));

      // Clear retry count on success
      retryCountRef.current.delete(pendingFile.id);

    } catch (error) {
      console.error('Upload failed:', error);
      
      // Handle retry logic
      const retryCount = retryCountRef.current.get(pendingFile.id) || 0;
      const shouldRetry = !isNonRetryableUploadError(error) && retryCount < maxRetries;

      if (shouldRetry) {
        retryCountRef.current.set(pendingFile.id, retryCount + 1);
        enqueueSnackbar(`Upload failed for ${pendingFile.file.name}. Retrying... (${retryCount + 1}/${maxRetries})`, { 
          variant: 'warning' 
        });
        
        // Retry after delay
        setTimeout(() => {
          uploadFile(pendingFile);
        }, 1000 * (retryCount + 1)); // Exponential backoff
      } else {
        // Mark as error after max retries
        setFiles(prev => prev.map(f => 
          f.id === pendingFile.id 
            ? { ...f, status: 'error', progress: 0 }
            : f
        ));
        
        const finalMessage = getUploadFailureMessage(error, pendingFile.file.name);
        enqueueSnackbar(finalMessage, { 
          variant: 'error' 
        });
        
        retryCountRef.current.delete(pendingFile.id);
      }
    }
  }, [enqueueSnackbar, maxRetries, presignPath]);

  const removeFile = useCallback((fileId) => {
    setFiles(prev => {
      const fileToRemove = prev.find(f => f.id === fileId);
      if (fileToRemove?.previewUrl) {
        cleanupPreviewUrl(fileToRemove.previewUrl);
      }
      retryCountRef.current.delete(fileId);
      return prev.filter(f => f.id !== fileId);
    });
  }, []);

  const reorderFiles = useCallback((oldIndex, newIndex) => {
    setFiles(prev => {
      const newFiles = [...prev];
      const [movedFile] = newFiles.splice(oldIndex, 1);
      newFiles.splice(newIndex, 0, movedFile);
      return newFiles;
    });
  }, []);

  const retryUpload = useCallback((fileId) => {
    const file = files.find(f => f.id === fileId);
    if (file && file.status === 'error') {
      retryCountRef.current.delete(fileId);
      uploadFile(file);
    }
  }, [files, uploadFile]);

  const refreshUpload = useCallback((fileId) => {
    const file = files.find(f => f.id === fileId);
    if (file && (file.status === 'done' || file.status === 'error')) {
      retryCountRef.current.delete(fileId);
      uploadFile(file);
    }
  }, [files, uploadFile]);

  const refreshUploadedFiles = useCallback(() => {
    files
      .filter(file => file.status === 'done')
      .forEach(file => {
        retryCountRef.current.delete(file.id);
        uploadFile(file);
      });
  }, [files, uploadFile]);

  const reset = useCallback(() => {
    // Clean up preview URLs
    files.forEach(file => {
      if (file.previewUrl) {
        cleanupPreviewUrl(file.previewUrl);
      }
    });
    
    setFiles([]);
    retryCountRef.current.clear();
  }, [files]);

  // Computed values
  const allUploaded = files.length > 0 && files.every(f => f.status === 'done');
  const hasUploading = files.some(f => f.status === 'uploading');
  const hasErrors = files.some(f => f.status === 'error');
  const uploadedFiles = files.filter(f => f.status === 'done');

  // Generate attachment metadata for API calls
  const getAttachmentMeta = useCallback(() => {
    return uploadedFiles.map(file => ({
      id: file.id,
      name: file.name,
      mime: file.mime,
      size: file.size,
      url: file.uploadedUrl,
      isImage: file.isImage
    }));
  }, [uploadedFiles]);

  return {
    files,
    addFiles,
    removeFile,
    reorderFiles,
    retryUpload,
    refreshUpload,
    refreshUploadedFiles,
    reset,
    allUploaded,
    hasUploading,
    hasErrors,
    uploadedFiles,
    getAttachmentMeta
  };
};
